// İSG uzmanı portalı — İş İzni enum yardımcıları (durum).
// Wire değerleri api-contract §14/§30.1'den; çeviri (etiket) çağıran ekrandadır (i18n key =
// `permits.status.<value>`). İzin tipi (eski `workType`) artık firma-tanımlı `PermitType`'tır
// (§22) — sabit enum değil; tip adı DTO'dan (`permitTypeName`) gösterilir, tip seçici
// `GET /permit-types`'ten beslenir. Tehlike sınıfı (hazard) sunumu S34-2'de
// `@isgportal/shared` `HazardTag`'e TAŞINDI (eski `hazardTagColor` — hazard artık bir DURUM
// `Tag`i gibi GÖRÜNMÜYOR, bkz. o dosyanın başlığı).
//
// `getPermitStatusPresentation`/`buildPermitProgressSteps` — S33-8 Pass B (coordinator
// bulgusu): TEK bir fonksiyon durum başına {labelKey, tagColor, tracker adımı} döner; header
// `Tag` (`PermitDetailPage.tsx`), liste/kuyruk ekranları (`PermitsListPage.tsx`,
// `DepartmentReviewQueuePage.tsx`, `DepartmentReviewDetailPage.tsx`) VE
// `buildPermitProgressSteps` HEPSİ bu fonksiyondan okur — iki ayrı yerde (eski
// `permitStatusTagColor` + `LIVE_STEP_INDEX`) yaşayan aynı kararın BİRBİRİNDEN AYRIŞMASI artık
// yapısal olarak imkânsız. Legacy `Approved` (api-contract §30.1: "eski tek-adımlı 'onaylandı'
// semantiği artık `AwaitingSupplierInfo`'dur") HER YERDE `AwaitingSupplierInfo` ile TAM aynı
// {label, renk, adım} alır — ayrı bir etiket/adım YOK. Gerçek `PermitStatus` (api-contract §14):
// `Draft, Submitted, AwaitingSupplierInfo, AwaitingAiReview, Revision, DepartmentReview,
// AwaitingEntryPermit, Active, Rejected, Expired, Closed` (+ legacy `Approved`) — README'nin
// jenerik `Draft → Submitted → Approved → Active → Closed` akışı DEĞİL (plan §6 Slice 3
// düzeltmesi).

import type {
  PermitStatus,
  ProgressStep,
  TagColor,
  WorkPermitActivityDto,
  WorkPermitActivityEvent,
  WorkPermitDto,
} from '@isgportal/shared';

// Yerel basit `TFunction` — depo kuralı (bkz. `lib/securityReason.ts`, `DayListTab.tsx`):
// react-i18next'in tam generic imzası yerine, yalnız bu dosyanın ihtiyacı olan çağrı şekli.
type TFunction = (key: string, options?: Record<string, unknown>) => string;

/**
 * Durum filtresi seçenekleri (türetilmiş `Expired` dahil; SPEC §1 onay akışı). Legacy `Approved`
 * filtrede gösterilmez — backend onu `AwaitingSupplierInfo` olarak yeniden yorumlar (union'da
 * etiket/ton için kalır). Sıra yaşam döngüsü akışına göre.
 */
export const PERMIT_STATUSES: PermitStatus[] = [
  'Draft',
  'Submitted',
  'AwaitingSupplierInfo',
  'AwaitingAiReview',
  'Revision',
  'DepartmentReview',
  'AwaitingEntryPermit',
  'Active',
  'Rejected',
  'Expired',
  'Closed',
];

/** Tehlike sınıfı seçenekleri (1=Az / 2=Tehlikeli / 3=Çok Tehlikeli; §14). */
export const HAZARD_CLASSES = [1, 2, 3] as const;

/**
 * İzin **form ile düzenlenebilir mi** (SPEC §14 + §5): yalnız `Draft`/`Submitted`. Bu yardımcı
 * detaydaki "Düzenle" (PUT `/permits/{uuid}`) butonunu kapılar; açan Draft'ta, reviewer Submitted'a
 * kadar düzenler. SPEC §5'teki "reviewer sonraki aşamalarda bilgi değiştirir (PATCH → SystemLog)"
 * akışı ayrıca **mevcut kullanıcının reviewer grant'ı** sinyali ister — o sinyal FE'ye gelince
 * (ayrı iş) genişletilecek; şimdilik açan için güvenli küme korunur (aksi hâlde açana 403 görünürdü).
 * Status'u backend döndürür — UI kendi hesaplamaz.
 */
export function isPermitEditable(status: PermitStatus): boolean {
  return status === 'Draft' || status === 'Submitted';
}

/** Tracker adımının o statüdeki görsel durumu — bkz. `buildPermitProgressSteps`. `null` yalnız
 * üç terminal statüde (`Rejected`/`Expired`/`Closed`): bunlar 5 adımlık normal diziye bir INDEKS
 * olarak oturmaz, kendi ayrı dal/adımlarını taşır (bkz. `buildPermitProgressSteps`). */
export type PermitStepState = 'current' | 'error' | null;

/**
 * Durum başına TEK karar kaynağı (S33-8 Pass B — dosya başı notu): i18n etiket anahtarı + `Tag`
 * rengi + tracker'daki konumu. Legacy `Approved` `AwaitingSupplierInfo` ile HER ALANDA (etiket/
 * renk/adım) birebir aynı satırı döner (api-contract §30.1).
 */
export interface PermitStatusPresentation {
  /** i18n anahtarı — `t(labelKey)` header `Tag`'i, liste/kuyruk ekranlarını VE tracker'ın canlı
   * adımının (review fazında) özel etiketini besler. */
  labelKey: string;
  /** `Tag` rengi — Carbon'un 4 renkli paletine (plan §5.1 — turuncu/sarı yok) eşlenir. */
  tagColor: TagColor;
  /** 5 adımlık normal tracker'daki (Draft·Submitted·Review·AwaitingEntryPermit·Active) indeks —
   * terminal statülerde (Rejected/Expired/Closed) `null`, bkz. `PermitStepState`. */
  stepIndex: 0 | 1 | 2 | 3 | 4 | null;
  /** `stepIndex`'teki görsel durum — yalnız `stepIndex` `null` değilken anlamlı. */
  stepState: PermitStepState;
}

export function getPermitStatusPresentation(status: PermitStatus): PermitStatusPresentation {
  switch (status) {
    case 'Draft':
      return {
        labelKey: 'permits.status.Draft',
        tagColor: 'gray',
        stepIndex: 0,
        stepState: 'current',
      };
    case 'Submitted':
      return {
        labelKey: 'permits.status.Submitted',
        tagColor: 'gray',
        stepIndex: 1,
        stepState: 'current',
      };
    // Legacy `Approved` — api-contract §30.1: eski tek-adımlı "onaylandı" semantiği artık
    // `AwaitingSupplierInfo`'dur; enum'da saklanmış eski satırlar için kalır, ama sunumu
    // `AwaitingSupplierInfo` ile TAM aynıdır (ayrı etiket/adım İCAT EDİLMEZ).
    case 'Approved':
    case 'AwaitingSupplierInfo':
      return {
        labelKey: 'permits.status.AwaitingSupplierInfo',
        tagColor: 'blue',
        stepIndex: 2,
        stepState: 'current',
      };
    case 'AwaitingAiReview':
      return {
        labelKey: 'permits.status.AwaitingAiReview',
        tagColor: 'gray',
        stepIndex: 2,
        stepState: 'current',
      };
    case 'DepartmentReview':
      return {
        labelKey: 'permits.status.DepartmentReview',
        tagColor: 'gray',
        stepIndex: 2,
        stepState: 'current',
      };
    case 'Revision':
      return {
        labelKey: 'permits.status.Revision',
        tagColor: 'gray',
        stepIndex: 2,
        stepState: 'error',
      };
    case 'AwaitingEntryPermit':
      return {
        labelKey: 'permits.status.AwaitingEntryPermit',
        tagColor: 'blue',
        stepIndex: 3,
        stepState: 'current',
      };
    case 'Active':
      return {
        labelKey: 'permits.status.Active',
        tagColor: 'green',
        stepIndex: 4,
        stepState: 'current',
      };
    case 'Rejected':
      return {
        labelKey: 'permits.status.Rejected',
        tagColor: 'red',
        stepIndex: null,
        stepState: 'error',
      };
    case 'Expired':
      return {
        labelKey: 'permits.status.Expired',
        tagColor: 'red',
        stepIndex: null,
        stepState: null,
      };
    case 'Closed':
      return {
        labelKey: 'permits.status.Closed',
        tagColor: 'gray',
        stepIndex: null,
        stepState: null,
      };
  }
}

/**
 * Uyarı satırının (InlineNotification) hangi metni kullanacağı (coordinator bulgusu, S33
 * fix): `AwaitingEntryPermit` "Giriş İzni Bekleniyor" — ONAY ZATEN VERİLDİ, yalnız saha girişi
 * bekleniyor (bkz. dosya başlığındaki PermitStatus listesi + `permits.status.AwaitingEntryPermit`
 * çevirisi) — bu yüzden "tam onayı engelliyor" metni buradan İTİBAREN yanlıştır. `Approved`
 * (Faz A/legacy — bkz. `PERMIT_STATUSES`'in başındaki not) AYNI sebeple: onay zaten verildi,
 * yalnız geçerlilik penceresi henüz başlamadı. `Active` aynı sebeple (onay geçmişte). `Expired`/
 * `Closed` da onayın ÖTESİNDE terminal durumlar. `Rejected` KASITLI OLARAK dışarıda bırakıldı —
 * onay hiç verilmedi, "engelliyor" metni hâlâ (eskimiş olsa da) YANLIŞ değil; üçüncü bir metin
 * İCAT ETMEK yerine (Türkçe ifadesinden emin olunmayan bir alan) var olan iki metinden DAHA AZ
 * YANLIŞ olana düşülür.
 */
export function permitIssuesPastApproval(status: PermitStatus): boolean {
  return (
    status === 'Approved' ||
    status === 'AwaitingEntryPermit' ||
    status === 'Active' ||
    status === 'Expired' ||
    status === 'Closed'
  );
}

function step(label: string, status: ProgressStep['status'], meta?: string): ProgressStep {
  return meta ? { label, status, meta } : { label, status };
}

/**
 * İzin ilerleme adımları (`ProgressIndicator`; S33-8 Pass B — konum/durum artık
 * `getPermitStatusPresentation`'dan okunur, burada İKİNCİ bir karar YOK). 5 görünür adım:
 * Draft · Submitted · Review (AwaitingSupplierInfo/AwaitingAiReview/DepartmentReview/Revision/
 * legacy Approved TEK adıma ÇÖKER) · Entry permit (AwaitingEntryPermit) · Active. Review adımı
 * CANLI (current/error) iken etiketi o statünün KENDİ etiketidir (header `Tag`'iyle AYNI anahtar)
 * — `complete`/`upcoming` iken jenerik `permits.progress.review`'a döner (coordinator bulgusu,
 * S33-8 Pass B). `Closed` Active'in ÖTESİNDE bir tamamlanma adımı olarak EKLENİR. `Rejected`/
 * `Expired` uç kırmızı/gri bir adım olarak EKLENİR — hangi ara adımdan geldiği etkinlik geçmişi
 * OKUNMADAN bilinemez, bu yüzden UYDURULMAZ (yalnız kesin bilinen — Draft her zaman geçilir —
 * tamam işaretlenir). Aktör/tarih meta'sı yalnız GERÇEKTEN var olan bir alandan gelir
 * (`approvedByName`/`formattedApprovedAt` — biçimlendirme çağıranın işi, bu dosya tarih/locale
 * bilmez); başka adımın böyle bir alanı YOK, orada meta hiç basılmaz (plan "omit unbacked data").
 */
export function buildPermitProgressSteps(
  permit: WorkPermitDto,
  t: TFunction,
  formattedApprovedAt: string | null,
): ProgressStep[] {
  const status = permit.status;
  const presentation = getPermitStatusPresentation(status);
  const labels = [
    t('permits.status.Draft'),
    t('permits.status.Submitted'),
    t('permits.progress.review'),
    t('permits.status.AwaitingEntryPermit'),
    t('permits.status.Active'),
  ];
  const activeMeta =
    permit.approvedByName && formattedApprovedAt
      ? t('permits.progress.activeMeta', {
          name: permit.approvedByName,
          date: formattedApprovedAt,
        })
      : undefined;

  if (status === 'Rejected') {
    // Draft/Submitted'tan olur (§14.2) — hangisinden geldiği bilinmiyor; yalnız Draft KESİN.
    return [step(labels[0] ?? '', 'complete'), step(t(presentation.labelKey), 'error')];
  }

  if (status === 'Expired') {
    // Yalnız Approved/Active'ten türetilir — izin Active'e KADAR gitmiş demektir. Uç adım
    // `'terminal'` (S33 Slice 7 QA fix, HIGH — `S33-7-final-qa-A.md` defekt #2): `'upcoming'`
    // "henüz gelinmedi" adımıyla AYNI soluk görünümü taşıyordu, bir izleyici "süresi doldu"yu
    // "buraya daha gelmedi"den ayırt EDEMİYORDU. `Rejected`in kırmızı `'error'`ıyla AYNI uç-
    // adım muamelesi, yalnız nötr/gri (izin geçerliliği bitti — bu bir HATA değil).
    return [
      ...labels.map((label, i) => step(label, 'complete', i === 4 ? activeMeta : undefined)),
      step(t(presentation.labelKey), 'terminal'),
    ];
  }

  if (status === 'Closed') {
    // Yalnız Approved/Active/Expired'tan kapanır — Active'in ÖTESİNDE bir tamamlanma adımı.
    return [
      ...labels.map((label, i) => step(label, 'complete', i === 4 ? activeMeta : undefined)),
      step(t(presentation.labelKey), 'complete'),
    ];
  }

  const index = presentation.stepIndex ?? 0;
  return labels.map((label, i) => {
    const state =
      i < index ? 'complete' : i === index ? (presentation.stepState ?? 'current') : 'upcoming';
    // Review adımı (2) CANLI iken statünün KENDİ etiketi — complete/upcoming iken jenerik kalır.
    const displayLabel = i === index && i === 2 ? t(presentation.labelKey) : label;
    return step(displayLabel, state, i === index && i === 4 ? activeMeta : undefined);
  });
}

// ============================================================================
// Aksiyon kaydı olayları (§35.1, owner review B3-3) — `PermitActivityPanel.tsx`'in tek karar
// kaynağı, aynı desen (S33-8 Pass B, dosya başlığı): etiket/sıfat İKİ AYRI yerde YAŞAMAZ.
// ============================================================================

/**
 * Aksiyon kaydı olayı → i18n etiket anahtarı (§35.1). `Record<WorkPermitActivityEvent, string>`
 * olarak tanımlı — görev talimatının "her olay bir i18n etiketi taşır" kuralı burada YAPISAL
 * olarak zorlanır: yeni bir olay eklenip haritaya eklenmezse TS DERLEME HATASI verir.
 */
export const PERMIT_ACTIVITY_EVENT_LABEL_KEYS: Record<WorkPermitActivityEvent, string> = {
  Created: 'permits.activities.event.Created',
  Submitted: 'permits.activities.event.Submitted',
  AutoApproved: 'permits.activities.event.AutoApproved',
  Approved: 'permits.activities.event.Approved',
  Rejected: 'permits.activities.event.Rejected',
  SupplierSubmitted: 'permits.activities.event.SupplierSubmitted',
  AiRevisionRequested: 'permits.activities.event.AiRevisionRequested',
  DepartmentReviewStarted: 'permits.activities.event.DepartmentReviewStarted',
  DepartmentRejected: 'permits.activities.event.DepartmentRejected',
  DepartmentReviewCompleted: 'permits.activities.event.DepartmentReviewCompleted',
  EntryPermitsIssued: 'permits.activities.event.EntryPermitsIssued',
  Closed: 'permits.activities.event.Closed',
  Expired: 'permits.activities.event.Expired',
  AiReviewCompleted: 'permits.activities.event.AiReviewCompleted',
  AiReviewFailed: 'permits.activities.event.AiReviewFailed',
  DepartmentApproved: 'permits.activities.event.DepartmentApproved',
  DepartmentEscalated: 'permits.activities.event.DepartmentEscalated',
  EntryGranted: 'permits.activities.event.EntryGranted',
  EntryRevoked: 'permits.activities.event.EntryRevoked',
  PersonnelAssigned: 'permits.activities.event.PersonnelAssigned',
  PersonnelUnassigned: 'permits.activities.event.PersonnelUnassigned',
  InfoUpdated: 'permits.activities.event.InfoUpdated',
  ProtectiveEquipmentAdded: 'permits.activities.event.ProtectiveEquipmentAdded',
  ProtectiveEquipmentRemoved: 'permits.activities.event.ProtectiveEquipmentRemoved',
  ControlItemAdded: 'permits.activities.event.ControlItemAdded',
  ControlItemRemoved: 'permits.activities.event.ControlItemRemoved',
  DocumentRequestAdded: 'permits.activities.event.DocumentRequestAdded',
  DocumentRequestRemoved: 'permits.activities.event.DocumentRequestRemoved',
};

/** `event`in `text`i (öge adı) İNTERPOLASYON olarak kullandığı olaylar (§35.1 "*Added/*Removed
 * olaylarında öge adı text'tedir") — diğer TÜM olaylarda `text` GÖRMEZDEN GELİNİR (satır kendi
 * başlığını yalnız yapılandırılmış alanlardan kurar, ham sunucu özetini TEKRARLAMAZ). */
const PERMIT_ACTIVITY_EVENTS_WITH_ITEM_TEXT = new Set<WorkPermitActivityEvent>([
  'ProtectiveEquipmentAdded',
  'ProtectiveEquipmentRemoved',
  'ControlItemAdded',
  'ControlItemRemoved',
  'DocumentRequestAdded',
  'DocumentRequestRemoved',
]);

/**
 * Bir aksiyon satırının BAŞLIK metni (§35.1) — `event` doluysa yapılandırılmış etiketten (gerekirse
 * `text`i öge adı olarak enjekte ederek) kurulur; `event` `null`sa (yorum + S35 öncesi log) `null`
 * döner — çağıran bu durumda ham `activity.text`e düşer (`PermitActivityPanel.tsx`'in eski
 * davranışı, DEĞİŞMEDİ — §35.1 "text yalnız event=null satırlarda gösterilir").
 */
export function permitActivityTitle(activity: WorkPermitActivityDto, t: TFunction): string | null {
  if (!activity.event) {
    return null;
  }
  const key = PERMIT_ACTIVITY_EVENT_LABEL_KEYS[activity.event];
  return PERMIT_ACTIVITY_EVENTS_WITH_ITEM_TEXT.has(activity.event)
    ? t(key, { item: activity.text })
    : t(key);
}

/**
 * Aktörün adı YANINDA gösterilecek İKİNCİL etiket — §35.1 "Rol gösterimi" normatif: kullanıcı rol
 * adı SNAPSHOT'ı YOKTUR, "eylemin sıfatı OLAYDAN okunur". `Supplier` sabit bir etikete sahiptir;
 * `Ai`/`System`'de `authorName` zaten `null` olduğundan bu ikisi için `null` döner (tek gösterilen
 * ad zaten "AI"/"Sistem"in kendisidir — ikinci kez TEKRAR EDİLMEZ). `User` için sıfat OLAYA göre
 * türetilir: onay/ret/giriş izni eylemleri → "İSG onaylayıcı", departman eylemleri →
 * `departments[0]`'ın adı, açılış eylemleri → "izni açan". Eşleşme yoksa (`InfoUpdated`, personel
 * ekle/çıkar, KKD/kontrol/ek belge, ya da `event = null` bir yorum) `null` — yalnız ad gösterilir,
 * sıfat UYDURULMAZ.
 */
export function permitActivityActorRoleKey(activity: WorkPermitActivityDto): string | null {
  switch (activity.actorType) {
    case 'Supplier':
      return 'permits.activities.actorRole.supplier';
    case 'Ai':
    case 'System':
      return null;
    case 'User':
      break;
  }
  switch (activity.event) {
    case 'Approved':
    case 'Rejected':
    case 'EntryPermitsIssued':
    case 'EntryGranted':
    case 'EntryRevoked':
      return 'permits.activities.actorRole.reviewer';
    case 'DepartmentApproved':
    case 'DepartmentRejected':
    case 'DepartmentReviewCompleted':
      return activity.departments && activity.departments.length > 0
        ? `permits.department.${activity.departments[0]}`
        : null;
    case 'Created':
    case 'Submitted':
    case 'AutoApproved':
      return 'permits.activities.actorRole.opener';
    default:
      return null;
  }
}
